import React, {memo} from "react";
import PropTypes from "prop-types";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Rectangle,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import humanizeString from "string-humanize";
import ViewLoadingSpinner from "../../Containers/Cell/View/ViewLoader";
import ZoomOutMessageContainer from "../../Containers/ZoomOutMessageContainer";
import {useHandleChartData} from "../../Helpers/useHandleChartData";

/** This component will generate Plot of type Bar.
 *
 * This component uses LineChart component of recharts library : https://recharts.github.io/en-US/api/BarChart
 */

const BarPlot = (props) => {
  const {properties} = props;
  const {axes = {}, legend = {}, line_keys: lines = [], xColumn} = properties;
  const {hide: legendHide = false} = legend;
  const xAxis = axes.x || {};
  const yAxis = axes.y || [];

  const {
    colorHexes,
    formatValue,
    isLoading,
    setXDomainLeft,
    setXDomainRight,
    showMessage,
    timeOnXAxis,
    timeOnYAxis,
    visibleData,
    xDomainLeft,
    xDomainRight,
    zoomIn,
    zoomOut,
  } = useHandleChartData(props);

  return (
    <ZoomOutMessageContainer showMessage={showMessage}>
      {isLoading ? (
        <ViewLoadingSpinner />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={visibleData}
            maxBarSize={30}
            onDoubleClick={zoomOut}
            onMouseDown={(nextState = {}) => setXDomainLeft(nextState.activeLabel || "")}
            onMouseMove={(nextState = {}) =>
              xDomainLeft && setXDomainRight(nextState.activeLabel || "")
            }
            onMouseUp={zoomIn}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              allowDataOverflow={true}
              dataKey={xColumn}
              domain={["auto", "auto"]}
              scale={timeOnXAxis ? "time" : undefined}
              tick={{fontSize: 10, fontFamily: "Roboto, Arial"}}
              tickFormatter={(value) => formatValue(value, "x", xAxis)}
              type={timeOnXAxis ? "number" : undefined}
              padding={{left: 20, right: 20}}
            />
            <YAxis
              scale={timeOnYAxis ? "time" : undefined}
              tick={{fontSize: 10, fontFamily: "Roboto, Arial"}}
              tickFormatter={(value) => formatValue(value, "y", yAxis[0])}
              type={timeOnYAxis ? "number" : undefined}
              width="auto"
            />
            <Tooltip
              cursor={{fill: "none"}}
              formatter={(value) => formatValue(value, "y", yAxis[0])}
              labelFormatter={(label) => formatValue(label, "x", xAxis)}
            />
            {!legendHide && <Legend />}
            {lines.map(({key}, index) => {
              if (!key) return null;
              return (
                <Bar
                  animationDuration={300}
                  activeBar={<Rectangle fill="#a1a1aa" />}
                  dataKey={key}
                  key={key}
                  name={humanizeString(key.replaceAll(".", "_"))}
                  fill={colorHexes[index]}
                />
              );
            })}
            {xDomainLeft && xDomainRight ? (
              <ReferenceArea
                opacity={0.2}
                x1={xDomainLeft}
                x2={xDomainRight}
                fill="#006dd9"
              />
            ) : null}
          </BarChart>
        </ResponsiveContainer>
      )}
    </ZoomOutMessageContainer>
  );
};

BarPlot.propTypes = {
  jsonData: PropTypes.array.isRequired,
  properties: PropTypes.object.isRequired,
  timeZone: PropTypes.string,
};

export default memo(BarPlot);
